// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@guestsupply/App/Components/Common/XngZone";
import XngCarousel, { SwiperStyle } from "@guestsupply/App/Components/XngCarousel/XngCarousel";
import xngGaGeneralEventEvent from "@guestsupply/App/Handlers/GoogleAnalytics/GeneralEvents/GeneralEvents";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { customXngageWidget, GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import CaretRight from "@guestsupply/XDL/Icons/CaretRight";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@guestsupply/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    image = "image",
    blogs = "blogs",
}

const enum blogsFields {
    title = "title",
    description = "description",
    link = "link",
    linkText = "linkText",
}

interface BlogsProps {
    fields: {
        [blogsFields.title]: string;
        [blogsFields.link]: LinkFieldValue;
        [blogsFields.description]: string;
        [blogsFields.linkText]: string;
    };
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.blogs]: BlogsProps[];
    };
}

// xng-change: add google event
const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    return {
        xGoogleAnalytics: state.xGoogleAnalytics,
    };
};

// xng-change: add google event
const mapDispatchToProps = {
    xngGaGeneralEventEvent,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory & OwnProps;

export interface XNewsListStyles {
    blogGridContainer?: GridItemProps;
    blogSectionGridItem?: GridItemProps;
    contentSectionGridItem?: GridItemProps;
    contentSectionWrapper?: InjectableCss;
    titleText?: TypographyProps;
    newsWrapper?: InjectableCss;
    summaryText?: TypographyPresentationProps;
    titleSummaryWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    swiperStyles?: SwiperStyle;
    blogWrapper?: GridItemProps;
}

export const newsListViewStyles: XNewsListStyles = {};

const styles = newsListViewStyles;

const Blogs = ({
    id,
    fields: { image, blogs },
    // xng-change: add google event
    xGoogleAnalytics,
    xngGaGeneralEventEvent,
}: Props) => {
    const contentSectionWrapper = mergeWithCss(styles.contentSectionWrapper, {
        css: css`
            background-image: url(${image});
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
        `,
    });

    const itemsLinks = useGetLinks(blogs, blog => blog.fields.link);
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));

    const blogsElements = blogs?.map((blog, index) => {
        return (
            <GridItem
                // eslint-disable-next-line react/no-array-index-key
                key={blog.fields.description}
                {...styles.blogWrapper}
            >
                <StyledWrapper
                    {...styles.newsWrapper}
                    // xng-change: add google event
                    onClick={() =>
                        xngGaGeneralEventEvent({
                            eventType: GOOGLE_EVENTS.SOLUTIONS_DESK_BLOG,
                            includeEvent: xGoogleAnalytics.includeSolutionsDeskBlogEvent,
                            clickText: blog.fields.title,
                            destination: itemsLinks?.[index].url,
                        })
                    }
                >
                    <StyledWrapper {...styles.titleSummaryWrapper}>
                        <Typography {...styles.titleText}>{blog.fields.title}</Typography>
                        {/* XNG-CHANGES : GUEST-2705 : Parsing html for description section */}
                        <Typography {...styles.summaryText}>{parse(blog.fields.description, parserOptions)}</Typography>
                    </StyledWrapper>
                    <Link href={itemsLinks?.[index].url} {...styles.readMoreLink} aria-label={blog.fields.linkText}>
                        {blog.fields.linkText}
                    </Link>
                </StyledWrapper>
            </GridItem>
        );
    });

    return (
        <GridContainer gap={isTabletScreen ? 0 : 22} {...styles.blogGridContainer}>
            <GridItem {...styles.blogSectionGridItem}>
                <XngZone contentId={id} hint="Title Section" zoneName="TitleSection"></XngZone>
                {isTabletScreen ? (
                    <XngCarousel
                        extendedStyles={styles.swiperStyles}
                        navigation
                        pagination
                        draggable={isTouchDevice()}
                        breakpoints={{
                            480: {
                                slidesPerView: 1,
                            },
                            768: {
                                slidesPerView: 2,
                                spaceBetween: 20,
                            },
                        }}
                    >
                        {blogsElements}
                    </XngCarousel>
                ) : (
                    <GridContainer gap={22}>{blogsElements}</GridContainer>
                )}
            </GridItem>
            <GridItem {...styles.contentSectionGridItem}>
                <StyledWrapper {...contentSectionWrapper}>
                    <XngZone contentId={id} hint="Content Section" zoneName="ContentSection"></XngZone>
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Blogs),
    definition: {
        group: customXngageWidget as any,
        displayName: "Blogs",
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: "Content Banner Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.blogs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: blogsFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: blogsFields.description,
                        editorTemplate: "RichTextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: blogsFields.link,
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                        isRequired: true,
                    },
                    {
                        name: blogsFields.linkText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
